import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Skeleton,
  Paper,
  Stack,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import MainLayout from '@/components/MainLayout';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { downloadCsv } from '@/lib/download';
import { toast } from '@/lib/toast';
import useNewParam from '@/lib/useNewParam';

const EMPTY_FORM = {
  member_id: '',
  service_id: '',
  quantity: 1,
  recurring: false,
  interval: 'monthly',
};

const initials = (name) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

// "Pending" is expectation, not danger — the tenant warning slot (pink on
// NORI) is wrong for it. Until an amber token exists in tenant_branding,
// use a fixed mode-aware amber.
const pendingColor = (t) => (t.palette.mode === 'dark' ? '#FFB74A' : '#B26A00');

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'recurring', label: 'Recurring' },
];

const money = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const shortDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

const sameMonth = (iso, ref = new Date()) => {
  if (!iso) return false;
  const d = new Date(iso);
  return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth();
};

const ROW_COLUMNS = {
  display: 'grid',
  gridTemplateColumns: 'minmax(160px, 1.2fr) 1.6fr 90px 120px 110px 120px',
  gap: 1.5,
  alignItems: 'center',
  px: 2.5,
};

function StatusCell({ status }) {
  const paid = status === 'paid';
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          flexShrink: 0,
          bgcolor: (t) => (paid ? t.palette.success.main : pendingColor(t)),
        }}
      />
      <Typography
        variant="body2"
        fontWeight={500}
        sx={{ color: (t) => (paid ? t.palette.success.main : pendingColor(t)) }}
      >
        {paid ? 'Paid' : 'Pending'}
      </Typography>
    </Stack>
  );
}

function SummaryCell({ label, value, sub, highlight, last }) {
  return (
    <Box
      sx={{
        p: '20px 24px',
        borderRight: { md: last ? 0 : 1 },
        borderBottom: { xs: last ? 0 : 1, md: 0 },
        borderColor: { xs: 'divider', md: 'divider' },
        ...(highlight && { bgcolor: (t) => alpha(t.palette.primary.main, 0.07) }),
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 1,
          fontWeight: highlight ? 600 : 500,
          color: highlight ? 'primary.main' : 'text.secondary',
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={1.25} alignItems="baseline">
        <Typography sx={{ fontSize: 30, fontWeight: 700, lineHeight: 1, color: highlight ? 'primary.main' : 'text.primary' }}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

const BillingPage = () => {
  const { isOwner } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [members, setMembers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchData = async () => {
    try {
      const [invRes, memRes, svcRes] = await Promise.all([
        api.get('/invoices'),
        api.get('/members'),
        api.get('/services'),
      ]);
      setInvoices(invRes.data);
      setMembers(memRes.data);
      setServices(svcRes.data);
    } catch {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useNewParam(() => setShowCreateDialog(true));

  const selectedService = services.find((s) => s.service_id === formData.service_id);
  const quantity = Math.max(1, parseInt(formData.quantity, 10) || 1);
  const total = selectedService ? Number(selectedService.price) * quantity : 0;

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      await api.post('/invoices', {
        member_id: formData.member_id,
        items: [{ service_id: formData.service_id, quantity }],
        recurring_interval: formData.recurring ? formData.interval : null,
      });
      toast.success('Invoice created successfully');
      setShowCreateDialog(false);
      setFormData(EMPTY_FORM);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handlePayNow = async () => {
    if (!selectedInvoice) return;
    try {
      const response = await api.post('/payments/checkout', {
        member_id: selectedInvoice.member_id,
        amount: selectedInvoice.amount,
        description: selectedInvoice.description,
      });
      window.location.href = response.data.checkout_url;
    } catch {
      toast.error('Failed to initiate payment');
    }
  };

  const summary = useMemo(() => {
    const pending = invoices.filter((i) => i.status === 'pending');
    const paidThisMonth = invoices.filter((i) => i.status === 'paid' && sameMonth(i.created_at));
    const recurring = invoices.filter((i) => i.recurring_interval);
    return {
      outstanding: pending.reduce((s, i) => s + Number(i.amount || 0), 0),
      pendingCount: pending.length,
      paidMonth: paidThisMonth.reduce((s, i) => s + Number(i.amount || 0), 0),
      paidCount: paidThisMonth.length,
      recurringTotal: recurring.reduce((s, i) => s + Number(i.amount || 0), 0),
      recurringCount: recurring.length,
    };
  }, [invoices]);

  const visibleInvoices = useMemo(() => {
    if (filter === 'pending') return invoices.filter((i) => i.status === 'pending');
    if (filter === 'paid') return invoices.filter((i) => i.status === 'paid');
    if (filter === 'recurring') return invoices.filter((i) => i.recurring_interval);
    return invoices;
  }, [invoices, filter]);

  const monthLabel = new Date().toLocaleDateString('en-US', { month: 'long' });

  return (
    <MainLayout title="Billing">
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {loading ? (
          <>
            <Skeleton variant="rounded" height={96} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={360} />
          </>
        ) : (
          <>
            <Paper variant="outlined" sx={{ mb: 2.25, overflow: 'hidden', borderRadius: 3.5 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr 1fr' } }}>
                <SummaryCell
                  label="Outstanding"
                  value={money(summary.outstanding)}
                  sub={`${summary.pendingCount} invoice${summary.pendingCount === 1 ? '' : 's'}`}
                  highlight
                />
                <SummaryCell
                  label={`Paid in ${monthLabel}`}
                  value={money(summary.paidMonth)}
                  sub={`${summary.paidCount} invoice${summary.paidCount === 1 ? '' : 's'}`}
                />
                <SummaryCell
                  label="Recurring"
                  value={money(summary.recurringTotal)}
                  sub={`${summary.recurringCount} plan${summary.recurringCount === 1 ? '' : 's'}`}
                  last
                />
              </Box>
            </Paper>

            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.75, flexWrap: 'wrap', gap: 0.75 }}>
              {FILTERS.map((f) => {
                const selected = filter === f.value;
                return (
                  <Chip
                    key={f.value}
                    label={f.label}
                    clickable
                    onClick={() => setFilter(f.value)}
                    data-testid={`invoice-filter-${f.value}`}
                    sx={{
                      borderRadius: 999,
                      fontWeight: selected ? 600 : 500,
                      ...(selected
                        ? { bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.main' } }
                        : { bgcolor: 'transparent', border: 1, borderColor: 'divider', color: 'text.secondary' }),
                    }}
                  />
                );
              })}
              <Box sx={{ flex: 1 }} />
              {isOwner && (
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<FileDownloadOutlinedIcon />}
                  disabled={exporting}
                  onClick={async () => {
                    setExporting(true);
                    try {
                      await downloadCsv('/invoices/export', 'invoices');
                    } catch {
                      toast.error('Failed to export CSV');
                    } finally {
                      setExporting(false);
                    }
                  }}
                  data-testid="export-invoices-button"
                  sx={{ borderColor: 'divider', color: 'text.secondary' }}
                >
                  {exporting ? 'Exporting…' : 'Export CSV'}
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateDialog(true)}
                data-testid="create-invoice-button"
              >
                Create invoice
              </Button>
            </Stack>

            {visibleInvoices.length === 0 ? (
              <Paper variant="outlined" sx={{ py: 8, textAlign: 'center' }}>
                <CreditCardIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary">
                  {invoices.length === 0 ? 'No invoices created yet' : 'No invoices match this filter'}
                </Typography>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                {/* Phones get stacked cards; the wide ledger needs >= md. */}
                <Stack sx={{ display: { xs: 'flex', md: 'none' } }}>
                  {visibleInvoices.map((invoice, idx) => {
                    const member = members.find((m) => m.member_id === invoice.member_id);
                    return (
                      <Box
                        key={invoice.invoice_id}
                        sx={{
                          p: 2,
                          borderBottom: idx === visibleInvoices.length - 1 ? 0 : 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {member?.name || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                              {invoice.description}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
                          >
                            {money(invoice.amount)}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 1 }}>
                          <StatusCell status={invoice.status} />
                          {invoice.recurring_interval && (
                            <Chip
                              icon={<AutorenewIcon sx={{ fontSize: 12 }} />}
                              label={invoice.recurring_interval}
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ height: 20, borderRadius: 999, fontSize: 11 }}
                            />
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {shortDate(invoice.created_at)}
                          </Typography>
                          <Box sx={{ flex: 1 }} />
                          {invoice.status === 'pending' && (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => {
                                setSelectedInvoice(invoice);
                                setShowPayDialog(true);
                              }}
                            >
                              Pay now
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>

                <Box sx={{ overflowX: 'auto', display: { xs: 'none', md: 'block' } }}>
                  <Box sx={{ minWidth: 900 }}>
                    <Box sx={{ ...ROW_COLUMNS, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                      {['Member', 'Description', 'Created', 'Status', 'Amount', ''].map((h, i) => (
                        <Typography
                          key={h || 'actions'}
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: 'text.secondary',
                            letterSpacing: '0.06em',
                            textAlign: h === 'Amount' ? 'right' : 'left',
                          }}
                        >
                          {h}
                        </Typography>
                      ))}
                    </Box>
                    {visibleInvoices.map((invoice, idx) => {
                      const member = members.find((m) => m.member_id === invoice.member_id);
                      return (
                        <Box
                          key={invoice.invoice_id}
                          data-testid={`invoice-${invoice.invoice_id}`}
                          sx={{
                            ...ROW_COLUMNS,
                            py: 1.25,
                            borderBottom: idx === visibleInvoices.length - 1 ? 0 : 1,
                            borderColor: 'divider',
                            '&:hover': { bgcolor: 'action.hover' },
                            transition: 'background-color 120ms ease',
                          }}
                        >
                          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                fontSize: 11,
                                fontWeight: 600,
                                bgcolor: (t) => alpha(t.palette.primary.main, 0.16),
                                color: 'primary.main',
                              }}
                            >
                              {initials(member?.name)}
                            </Avatar>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {member?.name || 'Unknown'}
                            </Typography>
                          </Stack>

                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {invoice.description}
                            </Typography>
                            {invoice.recurring_interval && (
                              <Chip
                                icon={<AutorenewIcon sx={{ fontSize: 12 }} />}
                                label={invoice.recurring_interval}
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ height: 20, borderRadius: 999, fontSize: 11, flexShrink: 0 }}
                              />
                            )}
                          </Stack>

                          <Typography variant="body2" color="text.secondary" noWrap>
                            {shortDate(invoice.created_at)}
                          </Typography>

                          <StatusCell status={invoice.status} />

                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                          >
                            {money(invoice.amount)}
                          </Typography>

                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {invoice.status === 'pending' && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  setSelectedInvoice(invoice);
                                  setShowPayDialog(true);
                                }}
                                data-testid={`pay-invoice-${invoice.invoice_id}`}
                              >
                                Pay now
                              </Button>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Paper>
            )}
          </>
        )}

        <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} maxWidth="sm" fullWidth>
          <form onSubmit={handleCreateInvoice}>
            <DialogTitle sx={{ fontWeight: 700 }}>Create invoice</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  select
                  required
                  label="Member"
                  fullWidth
                  value={formData.member_id}
                  onChange={(e) => setFormData({ ...formData, member_id: e.target.value })}
                >
                  {members.map((mem) => (
                    <MenuItem key={mem.member_id} value={mem.member_id}>
                      {mem.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  required
                  label="Service"
                  fullWidth
                  value={formData.service_id}
                  onChange={(e) => setFormData({ ...formData, service_id: e.target.value })}
                  helperText="Invoices bill previously declared services"
                >
                  {services.map((svc) => (
                    <MenuItem key={svc.service_id} value={svc.service_id}>
                      {svc.name} — ${Number(svc.price).toFixed(2)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Quantity"
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  fullWidth
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.recurring}
                      onChange={(e) => setFormData({ ...formData, recurring: e.target.checked })}
                    />
                  }
                  label="Recurring"
                />
                {formData.recurring && (
                  <>
                    <TextField
                      select
                      label="Repeats"
                      fullWidth
                      value={formData.interval}
                      onChange={(e) => setFormData({ ...formData, interval: e.target.value })}
                    >
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </TextField>
                    {/* TODO(recurring-payments): automatic charging via Stripe
                        subscriptions is not hooked up yet — this only marks the
                        invoice as recurring. */}
                    <Typography variant="caption" color="text.secondary">
                      Automatic recurring charges are coming soon — for now this marks the
                      invoice as recurring.
                    </Typography>
                  </>
                )}
                <Typography variant="subtitle1" fontWeight={700}>
                  Total: ${total.toFixed(2)}
                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button type="submit" variant="contained">
                Create
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <Dialog open={showPayDialog} onClose={() => setShowPayDialog(false)}>
          <DialogTitle sx={{ fontWeight: 700 }}>Proceed to payment</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              You will be redirected to Stripe to complete the payment of{' '}
              <Box component="span" color="primary.main" fontWeight={700}>
                ${selectedInvoice ? Number(selectedInvoice.amount).toFixed(2) : '0.00'}
              </Box>
              .
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowPayDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handlePayNow} fullWidth>
              Continue to Stripe
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default BillingPage;
